import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../admin.css';
import { Card, Table, Tag, Space, Button, Modal, Typography, Form, Input, message, Select, Tooltip, Divider } from 'antd';
import { EyeOutlined, DeleteOutlined, PoweroffOutlined, CopyOutlined, CodeOutlined } from '@ant-design/icons';
import { API_BASE, authHeaders } from '../../config/api';

const { Title } = Typography;

const ManageSnippets = () => {
  const [rows, setRows] = useState([]);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewCode, setViewCode] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form] = Form.useForm();

  // Generator states
  const [genForm] = Form.useForm();
  const [siteId, setSiteId] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [code, setCode] = useState('');
  const [platform, setPlatform] = useState('All');
  const [loading, setLoading] = useState(false);
  const [businessWebsite, setBusinessWebsite] = useState(null);
  const [trackOpts, setTrackOpts] = useState({
    pageview: false,
    clicks: false,
    scroll: false,
    forms: false,
    downloads: false,
    errors: false,
    session: false,
    device: false,
    location: false,
    outbound: false,
    // Ecommerce tracking options
    view_item_list: false,
    select_item: false,
    view_item: false,
    add_to_cart: false,
    remove_from_cart: false,
    view_cart: false,
    begin_checkout: false,
    add_payment_info: false,
    add_shipping_info: false,
    purchase: false,
    refund: false,
    view_promotion: false,
    select_promotion: false,
    add_to_wishlist: false,
    remove_from_wishlist: false,
    product_search: false,
  });

  // Industry-specific metric categories
  const getMetricCategoriesForIndustry = (industry) => {
    const baseAnalytics = ['pageview','clicks','forms','outbound','scroll','downloads','errors','session'];
    const baseTechnical = ['device','location'];
    
    switch(industry) {
      case 'E-commerce':
        return {
          'E-Commerce Core': ['view_item_list','select_item','view_item','add_to_cart','remove_from_cart','view_cart','begin_checkout','purchase','refund'],
          'Payment & Shipping': ['add_payment_info','add_shipping_info'],
          'Promotions & Wishlist': ['view_promotion','select_promotion','add_to_wishlist','remove_from_wishlist'],
          'Product Discovery': ['product_search'],
          'Website Analytics': baseAnalytics,
          'Technical Data': baseTechnical
        };
      case 'Non-profit organization':
        return {
          'Donation & Engagement': ['pageview','clicks','forms','outbound'],
          'Content Interaction': ['scroll','downloads'],
          'Event Tracking': ['session','errors'],
          'Technical Data': baseTechnical
        };
      case 'SaaS product / web app':
        return {
          'User Engagement': ['pageview','clicks','forms','session'],
          'Feature Usage': ['scroll','downloads','outbound'],
          'Technical Monitoring': ['errors','device'],
          'Location Data': ['location']
        };
      case 'Insurance company':
        return {
          'Quote & Application': ['pageview','clicks','forms'],
          'Document Interaction': ['downloads','outbound'],
          'User Journey': ['scroll','session'],
          'Technical Data': ['errors','device','location']
        };
      case 'Digital marketing agency':
        return {
          'Campaign Tracking': ['pageview','clicks','outbound'],
          'Lead Generation': ['forms','downloads'],
          'Engagement Metrics': ['scroll','session'],
          'Technical Analytics': ['errors','device','location']
        };
      case 'SME or small business':
        return {
          'Customer Journey': ['pageview','clicks','forms'],
          'Content Engagement': ['scroll','downloads','outbound'],
          'Business Metrics': ['session','errors'],
          'Technical Data': ['device','location']
        };
      case 'University or educational institution':
        return {
          'Student Engagement': ['pageview','clicks','forms'],
          'Resource Access': ['downloads','outbound'],
          'Learning Analytics': ['scroll','session'],
          'Technical Data': ['errors','device','location']
        };
      default:
        return {
          'E-Commerce': ['view_item_list','select_item','view_item','add_to_cart','remove_from_cart','view_cart','begin_checkout','add_payment_info','add_shipping_info','purchase','refund'],
          'Promotions & Wishlist': ['view_promotion','select_promotion','add_to_wishlist','remove_from_wishlist'],
          'Search & Discovery': ['product_search'],
          'Website Analytics': baseAnalytics,
          'Technical Data': baseTechnical
        };
    }
  };


  const optionsFrom = (keys) => keys.map(k => ({ value: k, label: (
    {
      view_item_list: 'View Item List',
      select_item: 'Select Item',
      view_item: 'View Item',
      add_to_cart: 'Add to Cart',
      remove_from_cart: 'Remove from Cart',
      view_cart: 'View Cart',
      begin_checkout: 'Begin Checkout',
      add_payment_info: 'Add Payment Info',
      add_shipping_info: 'Add Shipping Info',
      purchase: 'Purchase',
      refund: 'Refund',
      view_promotion: 'View Promotion',
      select_promotion: 'Select Promotion',
      add_to_wishlist: 'Add to Wishlist',
      remove_from_wishlist: 'Remove from Wishlist',
      product_search: 'Product Search',
      pageview: 'Pageviews',
      clicks: 'Clicks',
      forms: 'Form Submissions',
      outbound: 'Outbound Links',
      scroll: 'Scroll Depth',
      downloads: 'File Downloads',
      errors: 'JavaScript Errors',
      session: 'Session Duration',
      device: 'Device Info',
      location: 'Location (Privacy)'
    }[k]
  ) }));

  const selectedOf = (keys) => keys.filter(k => !!trackOpts[k]);
  const updateSelection = (keys, values) => {
    setTrackOpts(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = values.includes(k); });
      return next;
    });
  };

  // API helpers - using centralized config
  const apiBase = API_BASE;

  // Websites for mapping and select
  const [websites, setWebsites] = useState([]); // [{id,url,status,created_at,industry}]
  const [selectedWebsite, setSelectedWebsite] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch user's websites, snippets and business account
      const [wRes, sRes, baRes] = await Promise.all([
        fetch(`${apiBase}/my/websites`, { headers: { ...authHeaders() } }),
        fetch(`${apiBase}/my/snippets`, { headers: { ...authHeaders() } }),
        fetch(`${apiBase}/my/business-account`, { headers: { ...authHeaders() } })
      ]);
      if (!wRes.ok) throw new Error('Failed to load websites');
      if (!sRes.ok) throw new Error('Failed to load snippets');
      const wBody = await wRes.json().catch(() => ({}));
      const sBody = await sRes.json().catch(() => ({}));
      const baBody = await baRes.json().catch(() => (null));
      const wItems = Array.isArray(wBody?.items) ? wBody.items : Array.isArray(wBody) ? wBody : [];
      const sItems = Array.isArray(sBody?.items) ? sBody.items : Array.isArray(sBody) ? sBody : [];
      setBusinessWebsite(baBody?.website || null);
      // Build a local map to label rows even before setState flushes
      const wMap = new Map();
      wItems.forEach(w => wMap.set(Number(w.id), w));
      // Map to table rows
      const nextRows = sItems.map(s => ({
        key: s.id,
        id: s.id,
        website_id: s.website_id,
        website: wMap.get(Number(s.website_id))?.url || `#${s.website_id}`,
        is_active: !!s.is_active,
        snippet_code: s.snippet_code || ''
      }));
      setWebsites(wItems);
      setRows(nextRows);
    } catch (e) {
      message.error('Failed to load snippets');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { load(); }, [load]);

  // Generate snippet helpers
  const websiteOptions = useMemo(() => {
    return websites.map(w => ({ 
      label: `${(w.url || '').replace('https://','').replace('http://','')} ${w.industry ? `(${w.industry})` : ''}`, 
      value: String(w.id), 
      raw: w 
    }));
  }, [websites]);

  // Get current industry-specific categories
  const currentMetricCategories = useMemo(() => {
    if (!selectedWebsite?.industry) {
      return getMetricCategoriesForIndustry(null); // default categories
    }
    return getMetricCategoriesForIndustry(selectedWebsite.industry);
  }, [selectedWebsite]);

  const instructionContent = (p) => {
    switch (p) {
      case 'WordPress':
        return <Typography.Text>Dashboard → Appearance → Theme File Editor (or theme settings) → Footer or Custom Scripts → paste before <code>&lt;/body&gt;</code>; or use a header/footer plugin.</Typography.Text>;
      case 'Wix':
        return <Typography.Text>Settings → Advanced → Custom Code → Add Code → paste → All Pages → Place Code in: Body - End → Apply.</Typography.Text>;
      case 'GoDaddy':
        return <Typography.Text>Editor → Settings → Site-wide code injection (or Footer HTML) → paste → Publish.</Typography.Text>;
      case 'Shopify':
        return <Typography.Text>Online Store → Themes → Edit code → open <code>layout/theme.liquid</code> → paste before <code>&lt;/body&gt;</code> → Save. Or Theme settings → Custom liquid (Footer).</Typography.Text>;
      case 'Squarespace':
        return <Typography.Text>Settings → Developer Tools → Code Injection → Footer → paste → Save.</Typography.Text>;
      case 'Webflow':
        return <Typography.Text>Project Settings → Custom Code → Footer Code → paste → Save → Publish.</Typography.Text>;
      case 'Weebly':
        return <Typography.Text>Settings → SEO → Footer Code → paste → Publish.</Typography.Text>;
      case 'Joomla':
        return <Typography.Text>Extensions → Templates → Styles → your template → Edit <code>index.php</code> → paste before <code>&lt;/body&gt;</code>; or use a custom code plugin/module.</Typography.Text>;
      case 'Drupal':
        return <Typography.Text>Appearance → Theme settings → Footer scripts (if available); or add to <code>html.html.twig</code> before <code>&lt;/body&gt;</code>.</Typography.Text>;
      case 'Blogger':
        return <Typography.Text>Theme → Edit HTML → paste before <code>&lt;/body&gt;</code> → Save.</Typography.Text>;
      case 'Ghost':
        return <Typography.Text>Settings → Code Injection → Site Footer → paste → Save.</Typography.Text>;
      case 'Magento':
        return <Typography.Text>Content → Design → Configuration → Edit theme → HTML Head/Footer → Scripts → paste → Save &amp; flush cache.</Typography.Text>;
      case 'Custom':
        return <Typography.Text>Edit your template and paste right before <code>&lt;/body&gt;</code>.</Typography.Text>;
      default:
        return null;
    }
  };

  const buildSnippet = (id) => {
    const wantPage = !!trackOpts.pageview;
    const wantClicks = !!trackOpts.clicks;
    const wantScroll = !!trackOpts.scroll;
    const wantForms = !!trackOpts.forms;
    const wantDownloads = !!trackOpts.downloads;
    const wantErrors = !!trackOpts.errors;
    const wantSession = !!trackOpts.session;
    const wantDevice = !!trackOpts.device;
    const wantLocation = !!trackOpts.location;
    const wantOutbound = !!trackOpts.outbound;
    // Ecommerce flags
    const wantViewItemList = !!trackOpts.view_item_list;
    const wantSelectItem = !!trackOpts.select_item;
    const wantViewItem = !!trackOpts.view_item;
    const wantAddToCart = !!trackOpts.add_to_cart;
    const wantRemoveFromCart = !!trackOpts.remove_from_cart;
    const wantViewCart = !!trackOpts.view_cart;
    const wantBeginCheckout = !!trackOpts.begin_checkout;
    const wantAddPayment = !!trackOpts.add_payment_info;
    const wantAddShipping = !!trackOpts.add_shipping_info;
    const wantPurchase = !!trackOpts.purchase;
    const wantRefund = !!trackOpts.refund;
    const wantViewPromo = !!trackOpts.view_promotion;
    const wantSelectPromo = !!trackOpts.select_promotion;
    const wantAddWishlist = !!trackOpts.add_to_wishlist;
    const wantRemoveWishlist = !!trackOpts.remove_from_wishlist;
    const wantProductSearch = !!trackOpts.product_search;

    const lines = [];
    lines.push(`<!-- Asuma Tracking Snippet -->`);
    lines.push(`<script>(function(){`);
    lines.push(`  var SITE_ID = ${Number(id)};`);
    lines.push(`  var BASE = ${JSON.stringify(process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || 'https://api.asumahq.com')};`);
    lines.push(`  function sendEvent(type, data){try {var payload = { site_id: SITE_ID, type: type, page: location.href, referrer: document.referrer || null, ts: Date.now(), data: data || {} };if (navigator.sendBeacon) {var blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });navigator.sendBeacon(BASE + '/collect', blob);} else {fetch(BASE + '/collect', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true }).catch(function(){});} } catch (e) {}}`);
    lines.push(`  function pixel(type){try {var i = new Image(1,1);i.src = BASE + '/pixel.gif?s=' + encodeURIComponent(SITE_ID) + '&t=' + encodeURIComponent(type) + '&p=' + encodeURIComponent(location.href) + '&r=' + encodeURIComponent(document.referrer || '') + '&ts=' + Date.now();} catch (e) {}}`);
    if (wantPage) {
      lines.push(`  // Pageview`);
      lines.push(`  sendEvent('pageview'); pixel('pageview');`);
    }
    if (wantClicks) {
      lines.push(`  // Click tracking`);
      lines.push(`  document.addEventListener('click', function(ev){try {var el = ev.target || {};var info = { tag: (el.tagName||'').toLowerCase(), id: el.id || null, cls: (el.className? String(el.className).slice(0,120): null), txt: (el.innerText? String(el.innerText).slice(0,60): null), href: (el.closest && el.closest('a') ? el.closest('a').href : null) };sendEvent('click', info);} catch(e) {}}, true);`);
    }
    if (wantScroll) {
      lines.push(`  // Scroll depth tracking (25/50/75/100%)`);
      lines.push(`  (function(){ var sent={25:false,50:false,75:false,100:false}; function check(){ try { var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight); var st = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0; var vh = window.innerHeight || document.documentElement.clientHeight; var pct = Math.min(100, Math.floor(((st+vh)/h)*100)); [25,50,75,100].forEach(function(m){ if (!sent[m] && pct>=m){ sent[m]=true; sendEvent('scroll_depth', { percent:m }); } }); } catch(e) {} } window.addEventListener('scroll', function(){ try { if (window._sd_t) clearTimeout(window._sd_t); window._sd_t = setTimeout(check, 200); } catch(e) {} }, { passive:true }); check(); })();`);
    }
    if (wantForms) {
      lines.push(`  // Form submission tracking`);
      lines.push(`  document.addEventListener('submit', function(ev){try {var el = ev.target || {};var info = { tag: (el.tagName||'').toLowerCase(), id: el.id || null, cls: (el.className? String(el.className).slice(0,120): null), action: el.action || null, method: el.method || null };sendEvent('form_submit', info);} catch(e) {}}, true);`);
    }
    if (wantDownloads) {
      lines.push(`  // File download tracking (common extensions)`);
      lines.push(`  document.addEventListener('click', function(ev){ try { var a = (ev.target && ev.target.closest) ? ev.target.closest('a[href]') : null; if (!a) return; var href = a.getAttribute('href')||''; var exts = ['.pdf','.doc','.docx','.xls','.xlsx','.ppt','.pptx','.zip','.rar','.7z','.csv','.txt']; var low = href.toLowerCase(); var hit = exts.some(function(e){ return low.endsWith(e); }); if (hit){ sendEvent('file_download', { href: href }); } } catch(e) {} }, true);`);
    }
    if (wantErrors) {
      lines.push(`  // JavaScript error tracking`);
      lines.push(`  window.addEventListener('error', function(e){ try { var msg = e.message || 'error'; var src = (e.filename||'') + ':' + (e.lineno||0) + ':' + (e.colno||0); sendEvent('js_error', { message: msg, source: src }); } catch(_){} });`);
      lines.push(`  window.addEventListener('unhandledrejection', function(e){ try { var r = e.reason; var msg = (r && (r.message||r.toString())) || 'unhandledrejection'; sendEvent('js_error', { message: msg, source: 'promise' }); } catch(_){} });`);
    }
    if (wantSession) {
      lines.push(`  // Session duration (send on unload)`);
      lines.push(`  (function(){ var start = Date.now(); function send(){ try { var dur = Math.max(0, Date.now()-start); sendEvent('session_duration', { ms: dur }); } catch(e) {} } window.addEventListener('beforeunload', send); window.addEventListener('pagehide', send); })();`);
    }
    if (wantDevice) {
      lines.push(`  // Device info (UA, viewport)`);
      lines.push(`  (function(){ try { var info = { ua: navigator.userAgent || null, lang: navigator.language || null, vp_w: window.innerWidth||null, vp_h: window.innerHeight||null, dpr: window.devicePixelRatio||1 }; sendEvent('device_info', info); } catch(e) {} })();`);
    }
    if (wantOutbound) {
      lines.push(`  // Outbound link tracking`);
      lines.push(`  document.addEventListener('click', function(ev){try {var a = (ev.target && ev.target.closest) ? ev.target.closest('a[href]') : null; if (!a) return; var url; try { url = new URL(a.href); } catch(e) { return; } if (url.host && url.host !== location.host) { sendEvent('outbound', { href: a.href, text: (a.innerText? String(a.innerText).slice(0,60): null) }); } } catch(e) {}}, true);`);
    }
    if (wantLocation) {
      lines.push(`  // Location (privacy-friendly: requires user permission)`);
      lines.push(`  (function(){ try { if (!navigator.geolocation) return; navigator.geolocation.getCurrentPosition(function(pos){ try { var c = pos && pos.coords; if (!c) return; // coarse rounding ~2 decimals to reduce precision
        var lat = Math.round(c.latitude*100)/100; var lon = Math.round(c.longitude*100)/100; sendEvent('location', { lat: lat, lon: lon, acc: c.accuracy }); } catch(e) {} }, function(){}, { enableHighAccuracy: false, maximumAge: 3600000, timeout: 5000 }); } catch(e) {} })();`);
    }
    // Ecommerce tracking
    if (wantViewItemList) {
      lines.push(`  // Ecommerce: View Item List - tracks when users view product lists/categories`);
      lines.push(`  window.trackViewItemList = function(params){ try { sendEvent('view_item_list', { list_id: params.list_id || null, list_name: params.list_name || null, items: params.items || [] }); } catch(e) {} };`);
    }
    if (wantSelectItem) {
      lines.push(`  // Ecommerce: Select Item - tracks when users click on products`);
      lines.push(`  window.trackSelectItem = function(params){ try { sendEvent('select_item', { item_id: params.item_id || null, item_name: params.item_name || null, list_name: params.list_name || null, price: params.price || null }); } catch(e) {} };`);
    }
    if (wantViewItem) {
      lines.push(`  // Ecommerce: View Item - tracks product detail page views`);
      lines.push(`  window.trackViewItem = function(params){ try { sendEvent('view_item', { item_id: params.item_id || null, item_name: params.item_name || null, category: params.category || null, price: params.price || null, currency: params.currency || 'USD' }); } catch(e) {} };`);
    }
    if (wantAddToCart) {
      lines.push(`  // Ecommerce: Add to Cart - tracks when items are added to cart`);
      lines.push(`  window.trackAddToCart = function(params){ try { sendEvent('add_to_cart', { item_id: params.item_id || null, item_name: params.item_name || null, price: params.price || null, quantity: params.quantity || 1, currency: params.currency || 'USD' }); } catch(e) {} };`);
    }
    if (wantRemoveFromCart) {
      lines.push(`  // Ecommerce: Remove from Cart - tracks when items are removed from cart`);
      lines.push(`  window.trackRemoveFromCart = function(params){ try { sendEvent('remove_from_cart', { item_id: params.item_id || null, item_name: params.item_name || null, price: params.price || null, quantity: params.quantity || 1 }); } catch(e) {} };`);
    }
    if (wantViewCart) {
      lines.push(`  // Ecommerce: View Cart - tracks cart page views`);
      lines.push(`  window.trackViewCart = function(params){ try { sendEvent('view_cart', { cart_total: params.cart_total || null, items: params.items || [], currency: params.currency || 'USD' }); } catch(e) {} };`);
    }
    if (wantBeginCheckout) {
      lines.push(`  // Ecommerce: Begin Checkout - tracks checkout initiation`);
      lines.push(`  window.trackBeginCheckout = function(params){ try { sendEvent('begin_checkout', { cart_total: params.cart_total || null, items: params.items || [], currency: params.currency || 'USD', coupon: params.coupon || null }); } catch(e) {} };`);
    }
    if (wantAddPayment) {
      lines.push(`  // Ecommerce: Add Payment Info - tracks payment method selection`);
      lines.push(`  window.trackAddPaymentInfo = function(params){ try { sendEvent('add_payment_info', { payment_type: params.payment_type || null, cart_total: params.cart_total || null, currency: params.currency || 'USD' }); } catch(e) {} };`);
    }
    if (wantAddShipping) {
      lines.push(`  // Ecommerce: Add Shipping Info - tracks shipping method selection`);
      lines.push(`  window.trackAddShippingInfo = function(params){ try { sendEvent('add_shipping_info', { shipping_tier: params.shipping_tier || null, shipping_cost: params.shipping_cost || null, currency: params.currency || 'USD' }); } catch(e) {} };`);
    }
    if (wantPurchase) {
      lines.push(`  // Ecommerce: Purchase - tracks completed transactions`);
      lines.push(`  window.trackPurchase = function(params){ try { sendEvent('purchase', { transaction_id: params.transaction_id || null, value: params.value || null, tax: params.tax || null, shipping: params.shipping || null, currency: params.currency || 'USD', items: params.items || [], coupon: params.coupon || null }); } catch(e) {} };`);
    }
    if (wantRefund) {
      lines.push(`  // Ecommerce: Refund - tracks refunded transactions`);
      lines.push(`  window.trackRefund = function(params){ try { sendEvent('refund', { transaction_id: params.transaction_id || null, value: params.value || null, currency: params.currency || 'USD', items: params.items || [] }); } catch(e) {} };`);
    }
    if (wantViewPromo) {
      lines.push(`  // Ecommerce: View Promotion - tracks when promotions are viewed`);
      lines.push(`  window.trackViewPromotion = function(params){ try { sendEvent('view_promotion', { promotion_id: params.promotion_id || null, promotion_name: params.promotion_name || null, creative_name: params.creative_name || null, creative_slot: params.creative_slot || null }); } catch(e) {} };`);
    }
    if (wantSelectPromo) {
      lines.push(`  // Ecommerce: Select Promotion - tracks when promotions are clicked`);
      lines.push(`  window.trackSelectPromotion = function(params){ try { sendEvent('select_promotion', { promotion_id: params.promotion_id || null, promotion_name: params.promotion_name || null, creative_name: params.creative_name || null }); } catch(e) {} };`);
    }
    if (wantAddWishlist) {
      lines.push(`  // Ecommerce: Add to Wishlist - tracks items added to wishlist`);
      lines.push(`  window.trackAddToWishlist = function(params){ try { sendEvent('add_to_wishlist', { item_id: params.item_id || null, item_name: params.item_name || null, price: params.price || null, currency: params.currency || 'USD' }); } catch(e) {} };`);
    }
    if (wantRemoveWishlist) {
      lines.push(`  // Ecommerce: Remove from Wishlist - tracks items removed from wishlist`);
      lines.push(`  window.trackRemoveFromWishlist = function(params){ try { sendEvent('remove_from_wishlist', { item_id: params.item_id || null, item_name: params.item_name || null }); } catch(e) {} };`);
    }
    if (wantProductSearch) {
      lines.push(`  // Ecommerce: Product Search - tracks product searches`);
      lines.push(`  window.trackProductSearch = function(params){ try { sendEvent('product_search', { search_term: params.search_term || null, results_count: params.results_count || 0 }); } catch(e) {} };`);
    }
    lines.push(`})();</script>`);
    return lines.join('\n');
  };

  const onGenerate = async () => {
    const sid = siteId || genForm.getFieldValue('site');
    if (!sid) return message.warning('Please select a website');
    const snippet = buildSnippet(sid);
    setCode(snippet);
    setGenOpen(true);
  };

  const onSave = () => {
    (async () => {
      try {
        const sid = Number(siteId || genForm.getFieldValue('site'));
        const meta = {
          snippet_name: genForm.getFieldValue('name') || 'Tracking Snippet',
          snippet_type: genForm.getFieldValue('type') || 'tracking',
          status: genForm.getFieldValue('status') || 'active',
          placement: genForm.getFieldValue('placement') || 'body',
          priority: Number(genForm.getFieldValue('priority') || 0),
          tracking_options: { ...trackOpts },
        };
        const res = await fetch(`${apiBase}/my/snippets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ website_id: sid, snippet_code: code, is_active: meta.status === 'active', meta })
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Failed to save snippet');
        message.success('Snippet saved');
        setGenOpen(false);
        setCode('');
        await load();
      } catch (e) {
        message.error(e.message || 'Failed to save snippet');
      }
    })();
  };

  const onCopy = async () => {
    try { await navigator.clipboard.writeText(code); message.success('Copied'); } catch { message.warning('Copy failed'); }
  };

  const toggleStatus = async (record) => {
    try {
      const res = await fetch(`${apiBase}/my/snippets/${record.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ is_active: !record.is_active })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to update status');
      await load();
    } catch (e) {
      message.error(e.message || 'Failed to update status');
    }
  };

  const deleteSnippet = (record) => {
    Modal.confirm({
      title: 'Delete Snippet',
      content: `Delete snippet for ${record.website}?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => {
        (async () => {
          try {
            const res = await fetch(`${apiBase}/my/snippets/${record.id}`, {
              method: 'DELETE',
              headers: { ...authHeaders() }
            });
            if (!res.ok) {
              const body = await res.json().catch(() => ({}));
              throw new Error(body.error || 'Failed to delete');
            }
            await load();
          } catch (e) {
            message.error(e.message || 'Failed to delete');
          }
        })();
      }
    });
  };

  const openEdit = (record) => {
    setEditRecord(record);
    form.setFieldsValue({ code: record.snippet_code || '' });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const values = await form.validateFields();
      const res = await fetch(`${apiBase}/my/snippets/${editRecord.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ snippet_code: values.code })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to update snippet');
      message.success('Snippet updated');
      setEditOpen(false);
      setEditRecord(null);
      await load();
    } catch (e) {
      // validation failed
    }
  };

  const columns = [
    { title: 'Website', dataIndex: 'website', key: 'website' },
    { title: 'Type', key: 'snippet_type', width: 120, render: (_, r) => (r.meta?.snippet_type || 'tracking') },
    { title: 'Status', key: 'status', width: 120, render: (_, r) => (r.meta?.status || (r.is_active ? 'active' : 'inactive')) },
    {
      title: 'Active', dataIndex: 'is_active', key: 'is_active', width: 120,
      render: (a) => <Tag color={a ? 'green' : 'default'}>{a ? 'active' : 'inactive'}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 340,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => { setViewCode(record.snippet_code || '// No code'); setViewOpen(true); }}>View</Button>
          <Button size="small" onClick={() => openEdit(record)}>Edit</Button>
          <Button size="small" icon={<PoweroffOutlined />} onClick={() => toggleStatus(record)}>
            {record.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button size="small" icon={<DeleteOutlined />} danger disabled={false} onClick={() => deleteSnippet(record)}>Delete</Button>
        </Space>
      )
    }
  ];

  return (
    <div className="abo-content">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>Manage Snippets</Title>
        <Button onClick={load} loading={loading}>Refresh</Button>
      </div>

      {/* Generate Tracking Snippet */}
      <Card style={{ borderRadius: 14, boxShadow:'0 6px 24px rgba(15,23,42,0.06)', marginBottom: 16 }} loading={loading}>
        <Form layout="vertical" form={genForm}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            <Form.Item label="Select Website" name="site" rules={[{ required: true, message: 'Choose a website' }]}>
              <Select
                placeholder="Choose website"
                options={websiteOptions}
                onChange={(v) => {
                  setSiteId(v);
                  const website = websites.find(w => String(w.id) === String(v));
                  setSelectedWebsite(website);
                }}
                showSearch
                optionFilterProp="label"
                disabled={loading || websiteOptions.length === 0}
              />
            </Form.Item>
            <Form.Item label="Snippet Name" name="name" initialValue="Tracking Snippet">
              <Input placeholder="e.g., Tracking Snippet" />
            </Form.Item>
            <Form.Item label="Type" name="type" initialValue="tracking">
              <Select
                options={[
                  { value: 'analytics', label: 'Analytics' },
                  { value: 'tracking', label: 'Tracking' },
                  { value: 'marketing', label: 'Marketing' },
                  { value: 'custom', label: 'Custom' },
                  { value: 'seo', label: 'SEO' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Status" name="status" initialValue="active">
              <Select
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'draft', label: 'Draft' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Placement" name="placement" initialValue="body">
              <Select
                options={[
                  { value: 'head', label: 'Head' },
                  { value: 'body', label: 'Body' },
                  { value: 'footer', label: 'Footer' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Priority" name="priority" initialValue={0}>
              <Input type="number" placeholder="0" />
            </Form.Item>
            <Form.Item label="Track" style={{ gridColumn: '1 / -1' }}>
              {selectedWebsite && (
                <div style={{ marginBottom: 12, padding: 8, background: '#f0f9ff', borderRadius: 6, fontSize: 12, color: '#0369a1' }}>
                  <strong>Industry:</strong> {selectedWebsite.industry || 'Not specified'} - Showing relevant metrics for this industry
                </div>
              )}
              <div style={{ display:'grid', gridTemplateColumns: `repeat(${Math.min(Object.keys(currentMetricCategories).length, 6)}, 1fr)`, gap:12 }}>
                {Object.entries(currentMetricCategories).map(([categoryName, categoryKeys]) => (
                  <Card key={categoryName} size="small" style={{ borderRadius: 10 }} title={categoryName}>
                    <Select
                      mode="multiple"
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Select events"
                      options={optionsFrom(categoryKeys)}
                      value={selectedOf(categoryKeys)}
                      onChange={(vals) => updateSelection(categoryKeys, vals)}
                    />
                  </Card>
                ))}
              </div>
            </Form.Item>
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
            <div style={{ color:'#64748b' }}>
              {websiteOptions.length === 0
                ? 'No managed websites found. Add a website first from Websites > Manage Websites, or convert your business website below.'
                : 'This snippet tracks pageviews and clicks and sends them to your backend.'}
            </div>
            <Space>
              <Tooltip title="Create snippet for the selected website">
                <Button type="primary" icon={<CodeOutlined />} onClick={onGenerate} disabled={websiteOptions.length === 0}>
                  Generate
                </Button>
              </Tooltip>
              {websiteOptions.length === 0 && businessWebsite && (
                <Button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${apiBase}/my/websites`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...authHeaders() },
                        body: JSON.stringify({ url: businessWebsite, status: 'active' })
                      });
                      const body = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(body.error || 'Failed to create website');
                      message.success('Website created');
                      await load();
                      // preselect the newly added website if possible
                      const label = String(businessWebsite).replace('https://','').replace('http://','');
                      const opt = (websites || []).find(w => (w.url||'').includes(label));
                      if (opt) genForm.setFieldsValue({ site: String(opt.id) });
                    } catch (e) {
                      message.error(e.message || 'Failed to create website');
                    }
                  }}
                >
                  Use Business Website
                </Button>
              )}
            </Space>
          </div>
        </Form>
      </Card>

      <Card style={{ borderRadius: 14 }} bodyStyle={{ padding: 16 }} loading={loading}>
        <Table columns={columns} dataSource={rows} pagination={{ pageSize: 5 }} size="small" />
      </Card>

      <Modal
        title="Snippet Code"
        open={viewOpen}
        onOk={() => setViewOpen(false)}
        onCancel={() => setViewOpen(false)}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={760}
      >
        <pre style={{ background:'#0f172a', color:'#e2e8f0', padding:12, borderRadius:8, marginTop:12, overflow:'auto' }}>{viewCode}</pre>
      </Modal>

      <Modal
        title="Edit Snippet"
        open={editOpen}
        onOk={saveEdit}
        onCancel={() => { setEditOpen(false); setEditRecord(null); }}
        okText="Save"
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Code" name="code" rules={[{ required: true, message: 'Code is required' }]}>
            <Input.TextArea rows={12} placeholder="Paste or edit your snippet code here" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Generated Snippet Modal */}
      <Modal
        title={
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
            <span>Your Tracking Snippet</span>
            <span style={{ fontSize:12, color:'#94a3b8' }}>Site ID: {String(siteId || genForm.getFieldValue('site') || '')}</span>
          </div>
        }
        open={genOpen}
        onOk={onSave}
        onCancel={() => setGenOpen(false)}
        okText="Save"
        cancelText="Close"
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={onCopy}>Copy</Button>,
          <Button key="cancel" onClick={() => setGenOpen(false)}>Close</Button>,
          <Button key="save" type="primary" onClick={onSave}>Save</Button>,
        ]}
        width={720}
      >
        <Typography.Text type="secondary">Paste this before the closing </Typography.Text>
        <code>&lt;/body&gt;</code>
        <Typography.Text type="secondary"> tag of your website.</Typography.Text>
        <Divider style={{ margin:'12px 0 8px' }}>Install on your platform</Divider>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
          <Typography.Text type="secondary">Platform</Typography.Text>
          <Select size="small" style={{ minWidth: 220 }} value={platform} onChange={setPlatform}
            options={[
              { value: 'All', label: 'All' },
              { value: 'WordPress', label: 'WordPress' },
              { value: 'Wix', label: 'Wix' },
              { value: 'GoDaddy', label: 'GoDaddy Website Builder' },
              { value: 'Shopify', label: 'Shopify' },
              { value: 'Squarespace', label: 'Squarespace' },
              { value: 'Webflow', label: 'Webflow' },
              { value: 'Weebly', label: 'Weebly' },
              { value: 'Joomla', label: 'Joomla' },
              { value: 'Drupal', label: 'Drupal' },
              { value: 'Blogger', label: 'Blogger' },
              { value: 'Ghost', label: 'Ghost' },
              { value: 'Magento', label: 'Magento' },
              { value: 'Custom', label: 'Custom HTML' },
            ]}
          />
        </div>
        {platform !== 'All' && (
          <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', color:'#0f172a', borderRadius:10, padding:'10px 12px', marginBottom:12 }}>
            <div style={{ fontWeight:600, marginBottom:6 }}>{platform} instructions</div>
            <div style={{ fontSize:13, lineHeight:1.6 }}>{instructionContent(platform)}</div>
          </div>
        )}
        <Typography.Text type="secondary" style={{ display:'block', marginTop:12 }}>Events are sent to <code>/collect</code> with a pixel fallback at <code>/pixel.gif</code>. Ensure your backend base URL in the snippet is publicly reachable.</Typography.Text>
        <Divider style={{ margin:'14px 0' }}>Snippet</Divider>
        <div style={{ position:'relative', borderRadius:10, overflow:'hidden', boxShadow:'inset 0 0 0 1px rgba(148,163,184,0.12)' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', background:'#0b1220', color:'#94a3b8', fontSize:12 }}>
            <span>HTML</span>
            <Space>
              <Button size="small" onClick={onCopy} icon={<CopyOutlined />}>Copy</Button>
            </Space>
          </div>
          <pre style={{ background:'#0f172a', color:'#e2e8f0', padding:12, margin:0, maxHeight:360, borderTop:'1px solid rgba(148,163,184,0.12)', overflow:'auto', fontFamily:'ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace', fontSize:12, lineHeight:1.5 }}>
{code}
          </pre>
        </div>
      </Modal>
    </div>
  );
};

export default ManageSnippets;
